/* eslint-disable consistent-return */
import { animated, config, useTransition } from '@react-spring/web';
import { ColorSystemNegative1, ColorSystemPositive1 } from 'autogen/design-tokens/tokens';
import Icon from 'components/Icon';
import useAsync, { type StatusStates } from 'hooks/globals/useAsync';
import Spinner from 'modules/shared/circleSpinner';
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import Button from './Button';
import type IButton from './shared/types/button';

const Container = styled.div`
    width: 16px;
    height: 16px;
    max-width: 16px;
    max-height: 16px;
`;

const AnimatedContainer = animated<any>(Container);

const StyledButtonWithAnimatedStatuses = styled(Button)<{ status: StatusStates }>`
    ${({ status }) => {
        switch (status) {
            case 'success':
                return css`
                    background-color: ${ColorSystemPositive1};
                    &:focus,
                    &:hover {
                        background-color: ${ColorSystemPositive1};
                    }
                `;
            case 'error':
                return css`
                    background-color: ${ColorSystemNegative1};
                    &:focus,
                    &:hover {
                        background-color: ${ColorSystemNegative1};
                    }
                `;
            default:
        }
    }}

    cursor: ${($disabled) => ($disabled ? 'pointer' : 'pointer')};
    transition: background-color 500ms linear;
    ${({ theme }) => theme.ButtonWithAnimatedStatuses}
`;

interface IButtonWithAnimatedStatuses extends IButton {
    onClick: () => Promise<void>;
}

const ButtonWithAnimatedStatuses = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, IButtonWithAnimatedStatuses>(
    ({ onClick: asyncFunction, children, ...restProps }, ref) => {
        const { execute, status, reset } = useAsync(asyncFunction, false);
        const [delayedStatus, setDelayedStatus] = useState(status);
        const [canChangeState, setCanChangeState] = useState(true);
        const transition = useTransition(delayedStatus, {
            initial: { scale: 1 },
            from: { scale: 0 },
            enter: { scale: 1 },
            leave: { scale: 0 },
            exitBeforeEnter: true,
            config: config.stiff,
            onStart: () => setCanChangeState(false),
            onRest: () => {
                setCanChangeState(true);
            }
        });

        const components = {
            idle: <Icon type="BasketPlus" size={16} fill="currentColor" />,
            pending: <Spinner size={16} />,
            success: <Icon type="Checkmark" size={16} fill="currentColor" />,
            error: <Icon type="Alert" size={16} fill="currentColor" />
        };

        // We need to do this stuff due to a bug with the exitBeforeEnter property in react spring. If the state changes before the spring animation have finished the animations bugs out completely.
        useEffect(() => {
            let timer;
            if (canChangeState) {
                setDelayedStatus(status);

                if (status === 'success' || status === 'error') {
                    timer = setTimeout(() => {
                        reset();
                    }, 2000);
                }
            }

            return () => {
                if (timer) clearTimeout(timer);
            };
        }, [canChangeState, reset, status]);

        return transition((style, item) => (
            <StyledButtonWithAnimatedStatuses
                ref={ref}
                disabled={delayedStatus === 'pending' || delayedStatus === 'error'}
                status={delayedStatus}
                onClick={execute}
                {...restProps}
            >
                <AnimatedContainer style={style}>{components[item]}</AnimatedContainer>
            </StyledButtonWithAnimatedStatuses>
        ));
    }
);

export default ButtonWithAnimatedStatuses;
